import * as React from 'react';
import { Prompt } from 'react-router';
import { Link } from "react-router-dom";
import ReactHtmlParser from 'react-html-parser';

import { Formik, FormikProps, Form, Field, FieldProps  } from 'formik';
import * as Yup from 'yup';
import { Debugger } from '@fnv-core/debugging';
import { Slug } from '@fnv-core/utils';
import ScrollToError from '../presentational/ScrollToError';
import { DisplayFormikState } from '../presentational/DisplayFormik';
import { FormControlsHelper } from "../presentational/FormControls";
import * as FormControls from "../presentational/FormControls";
import { FieldVisibilityHelpers } from "../helpers/FieldVisibilityHelpers";
import { ValidationHelpers } from "../helpers/ValidationHelpers";
import { KenticoValidatorUtils } from "../kentico/KenticoUtils";
import { FormControllerHelper } from './helpers/FormControllerHelper';
import { FrontendDashboardPageType } from '../constants/Constants';
import { FormService } from '../services/FormService';
import { PopoverService } from '../services/PopoverService';
import { PrintViewService } from '../services/PrintViewService';
import { StringHelpers } from '../helpers/StringHelpers';
import { DatasourceOptionsService } from '../services/DatasourceOptionsService';
import { FormLabelHelpers } from '../formcontrols/helpers/FormLabelHelpers';
import { MijnFNVPageContext } from '../presentational/pages/helpers/MijnFNVPageContext';

import { getAllUrlParams } from '../../jslibs/urlparams';
import { getResourceString } from '../kentico/resource-strings';
import { IFormFieldDefinition, IVisibilityCondition } from "../interfaces/Fields";
import { SanitizeHTML } from "../helpers/SanitizeHtmlHelper";
import { IFormConfig } from '../interfaces/FormConfig';

interface onSubmitFunction {
  (values : any, includeCurrentValues? : boolean, onlySaveForm? : boolean) : void;
}

interface gotoPreviousStepFunction {
  (values: any) : void;
}


interface onRenderCustomFieldFunction {
  (renderCustomField : any, formController: any, formikBag: any) : any;
}

interface onRenderFormErrorFunction {
  () : any;
}

export interface FormProps { 
  formDefinitions: { [key: string]: IFormFieldDefinition[] }[];
  formName : string;
  formCMSName : string;
  onSubmit : onSubmitFunction;
  initialValues : any;
  renderCustomField: onRenderCustomFieldFunction;
  stepIndex: number;
  onGotoPreviousStep : gotoPreviousStepFunction;
  formValues : any;
  formConfig : IFormConfig;
  isSummaryStep : boolean;
  isSubmitStep : boolean;
  isSubmitting : boolean;
  readOnly : boolean;
  additionalCssClass: string;
  withLinkToFormPage? : boolean;
  linkFormPage? : string;
  linkText? : string;
  pageType: 'dashboard' | 'view' | 'edit';
  summaryIntro?: string;
  onSuccessPopupMessage?: string;
  onSuccessPopupMessageVisualCheck?: string;
  pageWithOnly1Step?: boolean;
  renderFormError?: onRenderFormErrorFunction;
  layoutType: string;
  isShowPollResults : boolean;
  pollResults: any;

  onSubmitSuccessButton1Link? : string;
  onSubmitSuccessButton1LinkText? : string;

  onSubmitSuccessButton2Link? : string;
  onSubmitSuccessButton2LinkText? : string;

  isPrintView? : boolean;
  wasOnSummaryStep? : boolean;
  visitedSteps?: any;
  includeCurrentPageAsBackAndSubmitUrl? : boolean;
  backTitleForCurrentPageBackLink? : string;

  formContainer? : any;

  onStoreValues? : (values: any) => void;

  steps: any;

  onFieldChange? : (formName: string, fieldName : string, value : any) => void;

  onCancelForm? : () => void;

  onSummaryBackToStepClick : (formName: string, event: any) => void;
}

export interface FormControllerState {
  isSetEditFieldsToReadonly : boolean;
  collapsableState : any;
}

export interface IForm {
  validationRules: any;
  fieldMapper: any;
}

export class FormController extends React.Component<FormProps, FormControllerState> {

  form: IForm;
  state: FormControllerState = {
    isSetEditFieldsToReadonly : false,
    collapsableState : {}
  };

  private _promptLeaveMessage = getResourceString("Form.Save.Warning");
  private _formikBag: FormikProps<any> = {} as FormikProps<any>;

  constructor(props: FormProps) {
    super(props);    

    this.validateField = this.validateField.bind(this);
    this.gotoPreviousStep = this.gotoPreviousStep.bind(this);
    this.isFieldVisible = this.isFieldVisible.bind(this);

    // TODO : create field yup-validations && fieldMapper
    
    this.form = {
      validationRules : {},
      fieldMapper : {}
    };

    PrintViewService.setPrintView(props.isPrintView || false);
  }

  revalidateForm = () : void => {

    // set all fields touched that have a value
    const touchedFields = Object.keys(this._formikBag.values).reduce((acc, field) => {
      if (this._formikBag.values[field]) {
        acc[field] = true;
      }
      return acc;
    }, {});
  
    // Set all filled fields as touched
    this._formikBag.setTouched(touchedFields, false);
  
    this._formikBag.validateForm();
  };

  componentDidMount() {
   
    FormService.setFormConfig(this.props.formDefinitions, this.props.formConfig);

    let formDefinition: IFormFieldDefinition[] = this.props.formDefinitions[this.props.formName];

    if (formDefinition) {
      formDefinition.map((formFieldDefinition, index) => {    

        let fieldType = formFieldDefinition.type;      

        if (this.props.readOnly) {
          if ((fieldType !== "custom") && (fieldType !== "link")) {
            if (!this.state.isSetEditFieldsToReadonly) {
              this.setState({isSetEditFieldsToReadonly : true});
            }
          }
        }

        if (formFieldDefinition.isDependentField) {
          this.setState({[formFieldDefinition.name] : ""} as any);
        }
      });
    }
    
    FormService.callFormRelatedDataCallback("", this.props.formName, {});
  }

  unmounted : boolean = false;
  componentWillUnmount() {
    this.unmounted = true;
  }

  setPayload = (payload: any) => {
    if (this.props.formContainer) {
      this.props.formContainer.setPayload(payload);
    } 
  }

  getPayload = () => {
    if (this.props.formContainer) {
      return this.props.formContainer.getPayload();
    } 
    return {};
  }

  hasFormEditableControlsThatWereSetToDisabled(formikBag: FormikProps<any>) {
    const formDefinition: IFormFieldDefinition[] = this.props.formDefinitions[this.props.formName];

    // Check if there is a field that is enabled, visible, and of an editable type
    const result = formDefinition.some((formFieldDefinition: IFormFieldDefinition) => {    

      const fieldType = formFieldDefinition.type;
      let isRealReadOnlyField = false;      

      if (formFieldDefinition.enabledCondition) {
        if (!formFieldDefinition.enabledCondition.kenticoMacroFunction(this.form.fieldMapper, formikBag.values[formFieldDefinition.name] || "", KenticoValidatorUtils)) {
          if ((fieldType != "custom") && (fieldType !== "link")) {
            isRealReadOnlyField = true;                               
          }
        }
      }

      let visible = true;
      
      if (formFieldDefinition.visibilityCondition) {
        formFieldDefinition.visibilityCondition.map((condition) => {
          if (!this.isFieldVisible(formikBag, condition, formFieldDefinition)) {
            visible = false;
          }          
        });      
      }

      if (this.props.readOnly && visible && !isRealReadOnlyField) {
        if ((fieldType !== "custom") && (fieldType !== "link") && (fieldType !== "title") && (fieldType !== "submit")) {
          return true;
        }
      }

      return false;
    });

    return result;
  }

  hasFormEditableControls(formikBag : any) {
    let formDefinition : Array<any> = this.props.formDefinitions[this.props.formName];
    let result = formDefinition.some((formFieldDefinition: any, index) => {    

      let fieldType = formFieldDefinition.type;
      let isFieldEnabled = true;      
      if (formFieldDefinition.enabledCondition !== undefined) {
        if (!formFieldDefinition.enabledCondition.kenticoMacroFunction(this.form.fieldMapper, formikBag.values[formFieldDefinition.name] || "", KenticoValidatorUtils)) {
          isFieldEnabled = false;                               
        }
      }

      if (formFieldDefinition.disabled) {
        if (formFieldDefinition.disabled === "true") {
          isFieldEnabled = false;     
        }
      }

      if ((fieldType === "custom") || (fieldType === "link")) {
        isFieldEnabled = false;
      }

      let visible = true;
      
      if (formFieldDefinition.visibilityCondition !== undefined && formFieldDefinition.visibilityCondition.length > 0) {
        formFieldDefinition.visibilityCondition.map((condition : any) => {
          if (!this.isFieldVisible(formikBag, condition, formFieldDefinition)) {
            visible = false;
          }          
        });      
      }

      if (visible && isFieldEnabled) {
        if ((fieldType !== "title") && (fieldType !== "submit")) {
          return true;
        }
      }

      return false;
    });

    return result;
  }

  hasFormOnlyNonRequiredControls(formikBag : any) {
    let formDefinition : Array<any> = this.props.formDefinitions[this.props.formName];
    let result = true;
    formDefinition.forEach((formFieldDefinition: any, index) => {    

      let fieldType = formFieldDefinition.type;
      let isFieldEnabled = true;      
      if (formFieldDefinition.enabledCondition !== undefined) {
        if (!formFieldDefinition.enabledCondition.kenticoMacroFunction(this.form.fieldMapper, formikBag.values[formFieldDefinition.name] || "", KenticoValidatorUtils)) {
          isFieldEnabled = false;                               
        }
      }

      if (formFieldDefinition.disabled) {
        if (formFieldDefinition.disabled === "true") {
          isFieldEnabled = false;     
        }
      }

      if ((fieldType === "custom") || (fieldType === "link")) {
        isFieldEnabled = false;
      }

      let visible = true;
      
      if (formFieldDefinition.visibilityCondition !== undefined && formFieldDefinition.visibilityCondition.length > 0) {
        formFieldDefinition.visibilityCondition.map((condition : any) => {
          if (!this.isFieldVisible(formikBag, condition, formFieldDefinition)) {
            visible = false;
          }          
        });      
      }

      if (visible && isFieldEnabled) {
        if ((fieldType !== "title") && (fieldType !== "submit")) {
          let isRequired = formFieldDefinition.validation['required'] !== undefined;
          if (isRequired) {
            result = false;
          }
        }
      }
    });

    return result;
  }

  isFormDirty(formikBag : any) {
    let formDefinition : Array<any> = this.props.formDefinitions[this.props.formName];
    let result = false;
    formDefinition.map((formFieldDefinition: any, index) => {    

      let fieldType = formFieldDefinition.type;
      let isFieldEnabled = true;      
      if (formFieldDefinition.enabledCondition !== undefined) {
        if (!formFieldDefinition.enabledCondition.kenticoMacroFunction(this.form.fieldMapper, formikBag.values[formFieldDefinition.name] || "", KenticoValidatorUtils)) {
          isFieldEnabled = false;                               
        }
      }

      if (formFieldDefinition.disabled) {
        if (formFieldDefinition.disabled === "true") {
          isFieldEnabled = false;     
        }
      }

      if ((fieldType === "custom") || (fieldType === "link")) {
        isFieldEnabled = false;
      }

      let visible = true;
      
      if (formFieldDefinition.visibilityCondition !== undefined && formFieldDefinition.visibilityCondition.length > 0) {
        formFieldDefinition.visibilityCondition.forEach((condition : any) => {
          if (!this.isFieldVisible(formikBag, condition, formFieldDefinition)) {
            visible = false;
          }          
        });      
      }

      if (visible && isFieldEnabled) {
        if ((fieldType !== "title") && (fieldType !== "submit")) {
          if (formikBag.values[formFieldDefinition.name] != formikBag.initialValues[formFieldDefinition.name]) {
            result = true;
          }
        }
      }
    });

    return result;
  }

  gotoPreviousStep(event) {
    event.preventDefault();

    if (!!this.props.formConfig.storeFormWhenStepBack) {
      this.submitToContainer(this._formikBag.values, false);
    } else {
      if (!this.props.isSubmitting && 
          !this.props.isSubmitStep && 
          this.props.formConfig.leavePageProtection) {                
        if (!confirm(this.props.formConfig.leavePageProtectionMessage || this._promptLeaveMessage)) {
          return false;
        }        
      }
    }
    
    this.props.onGotoPreviousStep(this._formikBag.values);
    return false;
  }

  isFieldVisible(formikBag: FormikProps<any>, condition: IVisibilityCondition, formFieldDefinition: IFormFieldDefinition) : boolean {

    let visible = true;
    if (condition.type !== undefined) {
      if (condition.type === "visibility") {
        visible = condition.visible;
      } else if (!FieldVisibilityHelpers.checkKenticoVisibilityRule(condition, formikBag, formFieldDefinition, this.form.fieldMapper)) {
        visible = false;
      }
    } else
    if (FieldVisibilityHelpers.isFormDefinedInConditionButNotEXistsInFormValues(condition, this.props.formValues) || 
        FieldVisibilityHelpers.isFormDefinedInConditionButFieldDoesntExistInForm(condition, this.props.formValues) ||
        FieldVisibilityHelpers.isArrayConditionAndFieldValueDoesntExist(condition, this.props.formValues) ||
        FieldVisibilityHelpers.isFieldValueNotEqualToConditionValue(condition, this.props.formValues) ||
        FieldVisibilityHelpers.isFieldObjectValueNotEqualToConditionValue(condition,  this.props.formValues) ||
        FieldVisibilityHelpers.isFormUndefinedOrCurrentFieldValueUndefinedOrEmpty(condition, formikBag) ||
        FieldVisibilityHelpers.isCurrentFieldObjectValueNotEqualToConditionValue(condition, formikBag) ||
        FieldVisibilityHelpers.isCurrentFieldValueNotEqualToConditionValue(condition, formikBag) ||
        FieldVisibilityHelpers.isFieldValueEmptyAndNotEmptyCondition(condition, formikBag) 
    ) {
      visible = false; 
    } else if (FieldVisibilityHelpers.isArrayCondition(condition)) {
      if (FieldVisibilityHelpers.isCurrentFieldValueNotInArrayCondition(condition, formikBag)) {
        visible = false;
      }
    } else if (FieldVisibilityHelpers.isCurrentFieldValueOfTypeStringWithSubstringCondition(condition, formikBag)) {      
      const subStringValue = (formikBag.values[condition.fieldName] as string).substring(condition.substring.start, condition.substring.end);
      
      if (FieldVisibilityHelpers.isNotEQConditionalConditionAndValuesAreEqual(condition, subStringValue)) {
        visible = false;
      }

      if (FieldVisibilityHelpers.isEQConditionAndValuesAreNotEqual(condition, subStringValue)) {
        visible = false;
      }
    }

    return visible;
  }

  createValidationShape(formFieldDefinition : any, validationSchemaShape : any, extraValidationRules? : any) {
          
      
    if (formFieldDefinition.validation !== undefined && formFieldDefinition.validation["dataType"] !== undefined) {
      let dataType = formFieldDefinition.validation["dataType"];

      if (dataType.kind == "lidnr") {
        ValidationHelpers.addLidNrValidator(formFieldDefinition, validationSchemaShape, dataType);
      } else
      if (dataType.kind == "number") {
        ValidationHelpers.addNumberValidator(formFieldDefinition, validationSchemaShape, dataType);
      } else
      if (dataType.kind == "date") {
        ValidationHelpers.addDateValidator(formFieldDefinition, validationSchemaShape, dataType);
      } else
      if (dataType.kind == "iban") {
        ValidationHelpers.addIbanValidator(formFieldDefinition, validationSchemaShape, dataType);
      } else
      if (dataType.kind == "postcodenl") {

        let doZipcodeValidation = true;
        if (formFieldDefinition.enableMaskConditions !== undefined) {
          if (!formFieldDefinition.enableMaskConditions.kenticoMacroFunction(
                  this.form.fieldMapper, 
                  "", 
                  KenticoValidatorUtils)) {
            doZipcodeValidation = false;
          }
        }

        if (doZipcodeValidation) {
          ValidationHelpers.addZipcodeValidator(formFieldDefinition, validationSchemaShape, dataType);
        } else {
          validationSchemaShape[formFieldDefinition.name] = Yup.string();
        }
      }
    } 
    else {
      validationSchemaShape[formFieldDefinition.name] = Yup.string();
    }

    if (formFieldDefinition.type == "checkbox" && formFieldDefinition.validation !== undefined && formFieldDefinition.validation["required"] !== undefined) {
      ValidationHelpers.addCheckboxValidator(formFieldDefinition, validationSchemaShape);
    }
    if (formFieldDefinition.type == "fileupload") {
      ValidationHelpers.addFileUploadValidator(formFieldDefinition, validationSchemaShape);
    }
    if (formFieldDefinition.type == "richtexteditor") {
      ValidationHelpers.addMaxSizeValidator(formFieldDefinition, validationSchemaShape);
    }

    if (formFieldDefinition.validation !== undefined && formFieldDefinition.validation["ageCheck"] !== undefined) {
      ValidationHelpers.addMaxAgeCheckValidator(formFieldDefinition, validationSchemaShape, formFieldDefinition.validation["ageCheck"]);
    }    

    for (const validationRuleKey in formFieldDefinition.validation) {
      if (validationRuleKey !== "dataType" && formFieldDefinition.validation.hasOwnProperty(validationRuleKey)) {          
        if (validationSchemaShape[formFieldDefinition.name] !== undefined && 
          validationSchemaShape[formFieldDefinition.name][validationRuleKey] !== undefined) {

          validationSchemaShape[formFieldDefinition.name] = 
            validationSchemaShape[formFieldDefinition.name][validationRuleKey](formFieldDefinition.validation[validationRuleKey].error);

        }
      }
    }

    if (extraValidationRules) {
      for (const validationRuleKey in extraValidationRules) {
        if (validationRuleKey !== "dataType" && extraValidationRules.hasOwnProperty(validationRuleKey)) {          
          if (validationSchemaShape[formFieldDefinition.name] !== undefined && 
            validationSchemaShape[formFieldDefinition.name][validationRuleKey] !== undefined) {
  
            validationSchemaShape[formFieldDefinition.name] = 
              validationSchemaShape[formFieldDefinition.name][validationRuleKey](extraValidationRules[validationRuleKey].error);
  
          }
        }
      }
    }

    return validationSchemaShape;
  }

  validateField(formFieldDefinition : any, formikBag : any) {
    this._formikBag = formikBag;

    try {
      let visible = true;

      if (!!formFieldDefinition.isSummary) {
        return;
      }

      let extraValidationRules : any = {};
      if (formFieldDefinition.visibilityCondition !== undefined && formFieldDefinition.visibilityCondition.length > 0) {

        formFieldDefinition.visibilityCondition.map((condition : any) => {
          if (!this.isFieldVisible(formikBag, condition, formFieldDefinition)) {
            visible = false;
          }          
        });      
      }

      if (visible && !!formFieldDefinition.hideWhenNoOptions) {
        visible = DatasourceOptionsService.getOptionsForForm(formFieldDefinition.name).length > 0;
      }

      if (visible && !!formFieldDefinition.requiredWhenOptions) {
        if (DatasourceOptionsService.getOptionsForForm(formFieldDefinition.name).length > 0) {
          extraValidationRules.required = {
            error: StringHelpers.formFieldRequiredFieldErrorMessage(FormLabelHelpers.getLabelWithoutAsterisk(formFieldDefinition.label))
          };
        }
      }

      if (visible) {
        let validationSchemaShape : any = {};

        validationSchemaShape = this.createValidationShape(formFieldDefinition, validationSchemaShape, extraValidationRules);

        var schema = Yup.object().shape(validationSchemaShape);
                  
        try {          
          schema.validateSync(formikBag.values);            
        } catch (error) {
          if (error.errors !== undefined && error.errors.length > 0) {
            return error.errors[0];
          }
        }

        if (formFieldDefinition.validation?.post && formikBag.values[formFieldDefinition.name] == formFieldDefinition.validation.post.value) {
          return formFieldDefinition.validation.post.error;
        }
      }

      let fieldType = formFieldDefinition.type;
      if (formFieldDefinition.enabledCondition !== undefined) {
          if (!formFieldDefinition.enabledCondition.kenticoMacroFunction(this.form.fieldMapper, formikBag.values[formFieldDefinition.name], KenticoValidatorUtils)) {
            if ((fieldType != "custom") && (fieldType !== "link")) {
              fieldType = "readOnly";
            }
          }
      }

      if (visible && 
          fieldType !== "readOnly" &&
          formFieldDefinition.validation !== undefined && 
          formFieldDefinition.validation["kenticoRules"] !== undefined) {

        let validationSchemaShape : any = {};

        validationSchemaShape = this.createValidationShape(formFieldDefinition, validationSchemaShape);          
        ValidationHelpers.addKenticoValidator(formFieldDefinition, validationSchemaShape, this.props.formDefinitions[this.props.formName], formikBag);
        var schema = Yup.object().shape(validationSchemaShape);
        try {
          schema.validateSync(formikBag.values);            
        } catch (error) {
          if (error.errors !== undefined && error.errors.length > 0) {
            return error.errors[0];
          } else 
          if (error.errors === undefined) {
            // tslint:disable-next-line
            console.log("validateField exception (kentico rule)", error, formFieldDefinition, formikBag)
          }
        }  
      } 
      
    } catch (ex) {
      // tslint:disable-next-line
      console.log("exception in validateField", ex, formFieldDefinition, formikBag);
    }
  }

  validateValueSet = (values : any) => {
    let isValid : boolean = true;

    let formDefinition = this.props.formDefinitions[this.props.formName];
    formDefinition.map((formFieldDefinition : any) => {
      if (!!formFieldDefinition.isSummary) {
        return;
      }
      let result = this.validateField(formFieldDefinition, this._formikBag);
      if (result && result !== "") {
        isValid = false;
      }
    });

    return isValid;
  }

  saveForm = (event) => {
    if (this.validateValueSet(this._formikBag.values)) {
      this.submitToContainer(this._formikBag.values, true, true, true);
    }
  }

  submitToContainer = (values: any, submit?: boolean, includeCurrentValues? : boolean, onlySaveForm? : boolean) => {
    let valuesToStore = {};
    
    if ((this.props.stepIndex > 0 && !this.props.isSummaryStep) || this.props.stepIndex == 0) {
      let formDefinition = this.props.formDefinitions[this.props.formName];
      formDefinition.map((formFieldDefinition : any) => {
        let visible = true;
        const fieldType = formFieldDefinition.type;
        if ((fieldType !== "custom") && 
            (fieldType !== "link") && 
            (fieldType !== "title") && 
            (fieldType !== "submit")) {
          
          if (!formFieldDefinition.ignoreVisibilityConditionDuringSubmit &&
              !this.props.formConfig.ignoreVisibilityConditionDuringSubmit &&
              formFieldDefinition.visibilityCondition !== undefined) {
            formFieldDefinition.visibilityCondition.map((condition : any) => {
              if (!this.isFieldVisible(this._formikBag, condition, formFieldDefinition)) {
                visible = false;
              } 
            });
          }
        }

        if (visible && values[formFieldDefinition.name] !== undefined) {
          valuesToStore[formFieldDefinition.name] = values[formFieldDefinition.name];
        }
      });
    } else {
      if((this.props.formConfig as any).addFormControlsToSummary){
        ((this.props.formConfig as any).addFormControlsToSummary || []).map((controlName, index) => {
          if (values[controlName]) {
            valuesToStore[controlName] = values[controlName];
          }
        });
      }
    }

    if (submit === undefined || !!submit) {
      this.props.onSubmit(valuesToStore, !!includeCurrentValues, !!onlySaveForm);
    } else {
      if (this.props.onStoreValues) {
        this.props.onStoreValues(values);
      }
    }
  }

  hasFormBlockIntelligence = () => {
    return !this.props.formConfig.noFormBlockIntelligence && 
        (this.props.pageType !== "view") && 
        (!this.props.readOnly);
  }

  clickAlternateSubmitButton = (event) => {
    // TODO : make this optional and force location.href
    if (!!this.props.formConfig.shouldExtraButtonFollowLink) {
      event.preventDefault();
      const onSubmitSuccessButtonLink = this.props.formConfig.onSubmitSuccessButton1Link 
        ? this.props.formConfig.onSubmitSuccessButton1Link 
        : this.props.formConfig.onSubmitSuccessButton2Link;

      if (onSubmitSuccessButtonLink.indexOf("http") == 0) {
        location.href = onSubmitSuccessButtonLink;
      } else {
        this.props.formContainer.redirectToUrl(onSubmitSuccessButtonLink);
      }
      return false;
    } else {
      this.props.formConfig.reactRouteOnSuccess = this.props.onSubmitSuccessButton1Link 
        ? this.props.onSubmitSuccessButton1Link 
        : this.props.onSubmitSuccessButton2Link;
    }
  }

  renderOptionalExtraButton = (disableSubmitButtonOnValidationError, formikBag) => {

    const onSubmitSuccessButtonLink = this.props.onSubmitSuccessButton1Link || this.props.formConfig.onSubmitSuccessButton1Link || 
      this.props.onSubmitSuccessButton2Link || this.props.formConfig.onSubmitSuccessButton2Link;
    const onSubmitSuccessButtonLinkText = this.props.onSubmitSuccessButton1LinkText || this.props.formConfig.onSubmitSuccessButton1LinkText || 
      this.props.onSubmitSuccessButton2LinkText || this.props.formConfig.onSubmitSuccessButton2LinkText;

    if (!onSubmitSuccessButtonLink || 
        !onSubmitSuccessButtonLinkText || 
        !this.props.isSubmitStep ) {
      return <></>;
    }

    if (this.props.formConfig.summarySendButtonCondition !== undefined) {
      return <></>;
    }

    const submitCssClass = "";
    const submitLabel = onSubmitSuccessButtonLinkText;
    let cssClasses = this.state != null && this.props.isSubmitting ? 
      ("btn btn-success btn-form-submit btn-disabled "  + submitCssClass) : 
      (this.props.isSubmitStep ? ("btn btn-success btn-form-submit " + submitCssClass) : ("btn btn-success " + submitCssClass));
    
    const onSubmitSuccessButtonLinkCssClass = this.props.formConfig.onSubmitSuccessButton1LinkCssClass 
      ? this.props.formConfig.onSubmitSuccessButton1LinkCssClass
      : this.props.formConfig.onSubmitSuccessButton2LinkCssClass;

    if (onSubmitSuccessButtonLinkCssClass !== undefined) {
      cssClasses = "btn " + onSubmitSuccessButtonLinkCssClass;
    }


    return <div className={"row no-gutters form__extra-buttons-" + this.props.formName }>      
      <div className="col d-flex justify-content-end form__button-next-step">
        {this.props.isSubmitting ? <span className="spinner-border"></span> : null }
        <button type="submit" 
          onClick={this.clickAlternateSubmitButton}
          disabled={(this.props != null && this.props.isSubmitting) || 
            (disableSubmitButtonOnValidationError && formikBag.errors !== undefined && Object.keys(formikBag.errors).length > 0) ||
            (disableSubmitButtonOnValidationError && !this.validateValueSet(this._formikBag.values))
          } 
          className={cssClasses}>{submitLabel}
            {this.props.isSubmitStep ? null : <span className="icon icon-open-chevron-right"></span>}
            </button>
      </div>
    </div>;
  }

  onPrintClick = (e) => {
    e.preventDefault();
    (window as any).print();
    return false;
  }

  checkSubmitButtonCondition = () => {

    let result = {
      "link" : "",
      "text" : "",
      "cssClass": ""
    };
    
    if (this.props.isSubmitStep && !this.props.isPrintView && this._formikBag) {
      let fieldValue;
      for (let form in this.props.formValues) {
        if (this.props.formValues.hasOwnProperty(form)) {

          if (this.props.formValues[form] &&
              this.props.formValues[form][this.props.formConfig.summarySendButtonCondition.fieldName]) {
            fieldValue = this.props.formValues[form][this.props.formConfig.summarySendButtonCondition.fieldName];        
          }
        }
      }
      
      if (fieldValue.value) {
        fieldValue = fieldValue.value;
      }

      if (fieldValue !== undefined) {        
        if (fieldValue == this.props.formConfig.summarySendButtonCondition.value) {
            result =  {
              "link" : this.props.formConfig.orgReactRouteOnSuccess,
              "text" : this.props.formConfig.summarySendButtonCondition.buttonLabelIf,
              "cssClass" : this.props.formConfig.summarySendButtonCondition.buttonLabelIfClass
            };
        } 
        else if(fieldValue == this.props.formConfig.summarySendButtonCondition.value1) {
          result = {
            "link": this.props.onSubmitSuccessButton1Link,
            "text": this.props.formConfig.summarySendButtonCondition.buttonLabelElseIf,
            "cssClass": this.props.formConfig.summarySendButtonCondition.buttonLabelElseIfClass
          };
        } else {
          result = {
            "link" : this.props.onSubmitSuccessButton2Link,
            "text" : this.props.formConfig.summarySendButtonCondition.buttonLabelElse,
            "cssClass" : this.props.formConfig.summarySendButtonCondition.buttonLabelElseClass
          };
        }
      }    
    }

    return result;
  }

  // TODO Sjors; WIP, this will be fixed in sprint 153.
  getNextPageLink = (url: string) => {
    let additionialInfo = "";    
    let additionalId = "";

    if (this.props.backTitleForCurrentPageBackLink !== undefined && 
      this.props.backTitleForCurrentPageBackLink !== "") {
      additionialInfo = "backTitleForCurrentPageBackLink=" + this.props.backTitleForCurrentPageBackLink;
    }

    let urlParams = getAllUrlParams(this.context.search);
    
    if(urlParams.additionalId){
      additionalId = `additionalId=${urlParams.additionalId}`;
      url = `${url}/${urlParams.additionalId}`;
    }

    if (urlParams.gobackUrl) {
        return urlParams.gobackUrl;
    }

    if (!!this.props.includeCurrentPageAsBackAndSubmitUrl) {            
      return url + "?gobackUrl=" + location.hash.slice(1) + 
      (additionialInfo !== "" ? `?${additionialInfo}` : "") + 
      `&${additionalId}`;
    }
    let urlQueryParameter = "?";
    if (url.indexOf("?") >= 0) {
      urlQueryParameter = "&";
    }
    return url + 
    (additionialInfo !== "" ? urlQueryParameter + additionialInfo : "") + 
    (additionalId !== "" ? urlQueryParameter + additionalId : "");
  }

  cancelButtonClick = (event) => {
    event.preventDefault();
    if (this.props.onCancelForm) {
      this.props.onCancelForm();
    }
    return false;
  }
  
  renderSubmitButton = (disableSubmitButtonOnValidationError, formikBag, submitLabel, submitCssClass) => {

    if (!!this.props.isShowPollResults) {
      return null;
    }

    let hideSubmitButtons = false;

    let saveFormButton = <></>;
    if (!!this.props.formConfig.showSaveFormButton && !this.props.isSubmitStep) {      
      saveFormButton = <button onClick={this.saveForm}
        type="submit"
        disabled={!!this.props.isSubmitting }
        className={(this.props.isSubmitting ? "btn-disabled " : "") + "btn btn-secondary form__save-form"}>{this.props.formConfig.showSaveFormButtonTitle || "Tussentijds opslaan"}</button>;
    }

    if (this.props.isSubmitStep && !!this.props.formConfig.hideSubmitButtons) {
      //return <></>;
      hideSubmitButtons = true;
    }
    
    let btnFormSubmitClassName = (this.props.isSubmitStep && (this.props.onSubmitSuccessButton1LinkText || this.props.onSubmitSuccessButton2LinkText) ? "" : "btn-form-submit ");
    if (this.props.formConfig.overrideBtnFormSubmitClassName !== undefined) {
      btnFormSubmitClassName = this.props.formConfig.overrideBtnFormSubmitClassName;
    }
    let formButtonsWithExtraButtonBelowIt = "";

    if (((this.props.onSubmitSuccessButton1Link && this.props.onSubmitSuccessButton1LinkText) || (this.props.onSubmitSuccessButton2Link && this.props.onSubmitSuccessButton2LinkText)) && this.props.isSubmitStep) {
      formButtonsWithExtraButtonBelowIt = "form__buttons-with-extra-buttonsbelow";
    }

    let printButton = <></>;
    if (!this.props.isPrintView && 
        this.props.formConfig.showPrintButton === true &&
        this.props.isSummaryStep && this.props.stepIndex > 0) {
      printButton = <div className="row no-gutters form__print-button">
        <a href="#" onClick={this.onPrintClick} className="btn btn-outline-primary"><span className="icon icon-print"></span>Print</a>
      </div>;
    }

    const hasFormEditableControls = this.hasFormEditableControls(formikBag);
    const hasFormOnlyNonRequiredControls = this.hasFormOnlyNonRequiredControls(formikBag);
    let disabledButtonCheck = 
      (!formikBag.dirty && !this.props.isSubmitStep &&
        !this.props.isSummaryStep && hasFormEditableControls && 
        !hasFormOnlyNonRequiredControls &&
        !this.props.wasOnSummaryStep &&
        (!this.props.visitedSteps || (this.props.visitedSteps && !this.props.visitedSteps[this.props.formName])) &&
        !this.props.formConfig.noDisabledButtonCheckForDirtyState        
      ) || 
      (!formikBag.dirty && this.props.isSubmitStep && 
        (this.props.stepIndex == 0) && hasFormEditableControls &&
        !hasFormOnlyNonRequiredControls && 
        !this.props.wasOnSummaryStep &&
        (!this.props.visitedSteps || (this.props.visitedSteps && !this.props.visitedSteps[this.props.formName])) &&
        !this.props.formConfig.noDisabledButtonCheckForDirtyState
      );    

    const hideButtonIfNoEditableControls = (!hasFormEditableControls && this.props.formConfig.hideButtonIfNoEditableControls) && 
      (!this.props.isSummaryStep || this.props.stepIndex == 0);

    if (this.props.formConfig.summarySendButtonCondition !== undefined) {
      const result = this.checkSubmitButtonCondition();
      if (result && result.text != "" && result.link != "") {
        submitLabel = result.text;
        submitCssClass = result.cssClass;
        this.props.formConfig.reactRouteOnSuccess = result.link;
      }
    }

    // check if recaptcha control exists, if it exists we want to show the Google policy text
    let showRecaptchaText = false;
    if (formikBag.values.hasOwnProperty('ReCaptcha')) {
        showRecaptchaText = true;
    }

    // custom dirty check, currently only used in the mijn-fnv popup , mainly
    // to handle the form to specify birthDate and firstName for lite accounts
    if (!!this.props.formConfig.submitButtonIsDisabledIfFormIsNotDirty && !this.isFormDirty(formikBag) ) {
      disabledButtonCheck = true;
    }
    return <>
      {printButton}
      <div className={"row no-gutters form__buttons-" + this.props.formName + " " + formButtonsWithExtraButtonBelowIt }>
        {(showRecaptchaText === true) ? this.renderRecaptchaText() : null}
        {submitLabel != "" && !this.props.readOnly ? 
          <React.Fragment>  
            {this.props.stepIndex > 0 ? <div className="col-auto form__vorig-stap-col">
              <a onClick={this.gotoPreviousStep} className="form__vorig-stap" href="#"><span className="icon icon-open-chevron-left"></span>{getResourceString("Form.Step.Previous")}</a>
            </div> : null}
            {(this.props.stepIndex == 0 && 
            this.props.formConfig.backButtonInFirstStep !== undefined) ? <Link
              className={this.props.formConfig.backButtonInFirstStep.cssClass} 
              to={this.props.formConfig.backButtonInFirstStep.url}>{this.props.formConfig.backButtonInFirstStep.label} 
            </Link>
            : null}
            {hideSubmitButtons ? null : <div className="col d-flex justify-content-end form__button-next-step">
              {!!this.props.formConfig.showCancelButton ? <a href="#" className="btn btn-outline-primary mr-2" onClick={this.cancelButtonClick}>{this.props.formConfig.cancelButtonTitle}</a> : null}
              {this.props.isSubmitting ? <span className="spinner-border"></span> : null }
              {saveFormButton}
              {!hideButtonIfNoEditableControls && <button type="submit" 
                disabled={(this.props != null && this.props.isSubmitting) || 
                  (formikBag.errors !== undefined && 
                    !this.props.formConfig.enableButtonOnValidationErrors &&
                    Object.keys(formikBag.errors).length > 0) ||
                  (!this.props.formConfig.noDirtyCheck &&
                    !this.props.formConfig.enableButtonOnValidationErrors &&
                    !this.validateValueSet(this._formikBag.values)) ||
                  disabledButtonCheck 
                } 
                className={this.state != null && this.props.isSubmitting ? 
                  ("btn btn-success " + btnFormSubmitClassName + " btn-disabled " + submitCssClass) : 
                  (this.props.isSubmitStep ? ("btn btn-success " + 
                    btnFormSubmitClassName + 
                    submitCssClass) : ("btn btn-success " + submitCssClass))}>{submitLabel}
                  {this.props.isSubmitStep ? null : <span className="icon icon-open-chevron-right"></span>}
                  </button>}
            </div>}
          </React.Fragment>
        : null}
        {this.props.withLinkToFormPage && 
          this.props.linkFormPage &&
          !!this.props.formConfig.hasLinkToFormPage
          ? <Link className="align-right margin-top-m btn btn-success" 
            to={this.getNextPageLink(this.props.linkFormPage)}><span>{this.props.linkText}<span className="ml-2 icon icon-open-chevron-right"></span></span></Link>
        : null} 
        {this.props.withLinkToFormPage && 
          this.props.linkFormPage &&
          this.props.pageType === "view" && 
          (this.hasFormEditableControlsThatWereSetToDisabled(formikBag) || this.props.formConfig.explicitNextButton)
          ? <Link className="align-right margin-top-m btn btn-outline-primary" to={this.getNextPageLink(this.props.linkFormPage)}><span>{this.props.linkText}</span></Link>
        : null}
        {this.props.withLinkToFormPage && 
          this.props.linkFormPage &&
          this.props.pageType === "dashboard"
          ? <Link className="link__single margin-top-m" to={this.getNextPageLink(this.props.linkFormPage)}><span>Bekijk alle gegevens</span><span className="icon icon-open-chevron-right"></span></Link>
        : null}
      </div>
    </>;
  }

  renderRecaptchaText = () => {
    // Injecting HTML, because resource string has HTML tags in it, so we need to sanitize it first
    return <span className='form-control-recaptcha__text'>
      <SanitizeHTML html={getResourceString("Form.Recaptcha.Text")}></SanitizeHTML>
    </span>;
  }

  onFormBackgroundClick = () => {
    PopoverService.closeAllPopovers();
  }

  getCurrentStepCaption = () => {
    let stepCaption = "";
    if (this.props.steps) {
      this.props.steps.map((step) => {
        if (step.formName === this.props.formName) {
          stepCaption = step.title;
        }
      });
    }
    return stepCaption;
  }

  checkShowPrompt = (formikBag) => {
    const result = !this.props.isPrintView && 
      !this.props.readOnly &&
      !this.props.isSubmitting &&
      !this.props.formContainer.state.isSubmitting &&
      !this.props.formContainer.state.isShowPostOk && 
      (formikBag.dirty || 
        this.props.wasOnSummaryStep ||
        this.props.visitedSteps[this.props.formName] || 
        (this.props.isSubmitStep && this.props.isSummaryStep));
    return result;
  }

  onToggleGroupCollapsable = (collapsableGroupTitle, initialCollapsableState, event) => {
    event.preventDefault();
    if (this.state.collapsableState[collapsableGroupTitle] !== undefined) {
      this.setState((state) => {
        let newCollapsableState = {...state.collapsableState};
        newCollapsableState[collapsableGroupTitle] = !newCollapsableState[collapsableGroupTitle];
        return {
          ...state,
          collapsableState: newCollapsableState
        };
      });
    } else {
      this.setState((state) => {
        let newCollapsableState = {...state.collapsableState};
        newCollapsableState[collapsableGroupTitle] = !initialCollapsableState;
        return {
          ...state,
          collapsableState: newCollapsableState
        };
      });
    }
    return false;
  }

  createBackToStepLink = (formDefinition : any[], section : string, formikBag : any) => {
    let backToFormStepElement = <></>;
    if (this.props.isSummaryStep &&
      formDefinition.length > 0 ) {
       formDefinition.map((formFieldDefinition: any, index) => {
         let visible = true;                    
         let renderFormElement = false;

         if (formFieldDefinition.section === undefined && section == "") {
           renderFormElement = true;                    
         } else
         if (formFieldDefinition.section !== undefined && section == formFieldDefinition.section) {
           renderFormElement = true;
         }
         if (formFieldDefinition.visibilityCondition !== undefined) {
           formFieldDefinition.visibilityCondition.map((condition : any) => {
             if (!this.isFieldVisible(formikBag, condition, formFieldDefinition)) {
               visible = false;
             } 
           });
         }
         let fieldType = formFieldDefinition.type;
         if (renderFormElement && visible) {
           if (fieldType == "custom" && formFieldDefinition.customType == 'summary') { 
             if (
               (formFieldDefinition.hideEditLink === undefined ||
               (formFieldDefinition.hideEditLink !== undefined && 
                 !formFieldDefinition.hideEditLink))) {
               backToFormStepElement = <div className="col-auto form-summary__edit-link">
                 <a href="#" 
                   data-formname={formFieldDefinition.form} 
                   onClick={this.props.onSummaryBackToStepClick.bind(this, formFieldDefinition.form)}>{getResourceString("Form.Summary.Modify") || "Wijzigen"}</a>
               </div>;
             }
           }
         }
     });
     return backToFormStepElement;
   }
  }

  getFieldKey = (formFieldDefinition , formikBag) => {
    let fieldKey = formFieldDefinition.name + "_formfield";
    let dependentValue = "";    
    if (Array.isArray(formFieldDefinition.dependentOnField)) {
      formFieldDefinition.dependentOnField.map(dependentField => {
        dependentValue = this.state[dependentField] || formikBag.values[dependentField];
        if (dependentValue && (dependentValue as any).value) {
            dependentValue = (dependentValue as any).value;
        }
        // force rerendering when dependent field changes
        fieldKey = fieldKey + "_" + dependentField + "_" + dependentValue;
      });
    } else
    if (formFieldDefinition.dependentOnField) {
        dependentValue = this.state[formFieldDefinition.dependentOnField] || formikBag.values[formFieldDefinition.dependentOnField];                        
        if (dependentValue && (dependentValue as any).value) {
            dependentValue = (dependentValue as any).value;
        }
        // force rerendering when dependent field changes
        fieldKey = fieldKey + "_" + formFieldDefinition.dependentOnField + "_" + dependentValue;
    }
    return fieldKey;
  }

  getAdditionalClassForCustomField = (formFieldDefinition , customType) => {
    if (customType === "tip") {
      if (formFieldDefinition && formFieldDefinition.tipLayout === "block") {
        return "form__custom-panel--place-outside-form-area";
      }      
    }
    return "";
  }

  private renderDebugInformation(formikBag: FormikProps<any>) {
    return (
      <>
        {Debugger.isEnabled() && 
          <DisplayFormikState {...formikBag}></DisplayFormikState>
        }
      </>
    );
  }

  render() {
    
    const urlParams = getAllUrlParams();

    FormService.setIsSummaryView(this.props.stepIndex > 0 && this.props.isSummaryStep);

    let formDefinition: IFormFieldDefinition[] = this.props.formDefinitions[this.props.formName];

    FormControllerHelper.showOrHideBlockOnSummarySteps(this.props.pageWithOnly1Step,  this.props.isSummaryStep);

    // formValues should be assigned with formikBag.values
    this.form.fieldMapper = FormControllerHelper.getFieldsMapper(
      formDefinition, 
      this,
      this.props.formValues,
      this.props.formDefinitions
    );

    let initialValues = {
      ...this.props.initialValues
    };

    let self = this;
    let submitLabel : string = "";
    let submitCssClass : string = "";
    let disableSubmitButtonOnValidationError : boolean = false;    
    
    formDefinition.forEach((formFieldDefinition: IFormFieldDefinition) => {

      if (initialValues[formFieldDefinition.name] == null) {
        initialValues[formFieldDefinition.name] = '';
      }

      if (formFieldDefinition.type === "submit") {
        submitLabel = formFieldDefinition.label;
        if (formFieldDefinition.cssClass !== undefined) {
          submitCssClass = formFieldDefinition.cssClass;
        }

        if (formFieldDefinition.disableOnValidationError !== undefined) {
          disableSubmitButtonOnValidationError = formFieldDefinition.disableOnValidationError;
        }
      } 
    });

    const sections: string[] = [''];
    const sectionCssClassNames: string[] = [''];
    formDefinition.forEach((formFieldDefinition: IFormFieldDefinition) => {
      if (formFieldDefinition.section !== undefined && formFieldDefinition.section != '') {
        if (sections.indexOf(formFieldDefinition.section) < 0) {
          sections.push(formFieldDefinition.section);
          sectionCssClassNames.push(formFieldDefinition.sectionCssClass || formFieldDefinition.section);
        }
      }
    });

    /*
      igv debuggen :
        toevoegen op veld niveau
        <DisplayFormikState {...form}></DisplayFormikState>
    */

    let additionalFormCssClass = "";
    if (this.props.formConfig.additionalCSSClass !== undefined) {
      additionalFormCssClass = " " + this.props.formConfig.additionalCSSClass;
    }

    if (this.props.additionalCssClass !== undefined) {
      additionalFormCssClass = " " + this.props.additionalCssClass;
    }
    
    if (this.props.pageType === "edit") {
      FormControllerHelper.showOrHideBackLink(this.props.stepIndex == 0);
    }

    let formGroupCount = 0 ;
    formDefinition.map((formFieldDefinition: IFormFieldDefinition) => {
      if (formFieldDefinition.type == "title") {
        formGroupCount++;
      }
    });
    
    if (this.props.isSummaryStep && !!this.props.formConfig.summaryValueBelowFields) {
      additionalFormCssClass = " form___summary--values-below-fields";
    }

    return <div id="fnv-form" 
      onClick={this.onFormBackgroundClick}
      className={"fnv-form__container fnv-form__container-" + this.props.formName + additionalFormCssClass + 
        (this.props.isPrintView ? " fnv-form__container--print-view" : "")}>
      {this.props.isSummaryStep && (this.props.summaryIntro || "") !== "" ? 
        FormControllerHelper.renderSummaryIntro(this.props.summaryIntro) : null }
      {this.props.readOnly ? null : <p className="form__form-required-tip">{this.props.formConfig.requiredFieldTip}</p>}
      <Formik
        ref="formik"
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnChange={false}
        validateOnBlur={true}
        onSubmit={(values: any) => {
          this.submitToContainer(values);
        }}

        children={(formikBag: FormikProps<any>) => {
          this._formikBag = formikBag;
          let placeFirstTitlePanelInSectionOutside = false;
          let formErrorWasRendered = false;                  
          
          let formTitleIndex = 0;

          return (<Form className={"form form__react form__" + this.props.formName}
            data-form-type={this.props.formCMSName || ""}  
            data-form-step={this.props.formName} 
            data-form-url={document.referrer || "DocumentReferrerIsLeeg"}            
            action={this.props.formConfig.actionUrl !== undefined ? this.props.formConfig.actionUrl : ""}
            method={this.props.formConfig.formMethod !== undefined ? this.props.formConfig.formMethod : ""}
          >
            {(!this.props.isPrintView && this.props.formConfig.leavePageProtection) && <Prompt                
                when={this.checkShowPrompt(formikBag)}                
                message={this.props.formConfig.leavePageProtectionMessage || this._promptLeaveMessage}
            />}

            {this.renderDebugInformation(formikBag)}

            <ScrollToError {...formikBag} />

            {sections.map((section : string, index : number) => {
              let sectionBreakClass = "";
              let formGroupCssClass = "";
              let sectionCssClass = sectionCssClassNames[index] || section;

              if (this.props.pageType !== FrontendDashboardPageType && this.hasFormBlockIntelligence()) {
                placeFirstTitlePanelInSectionOutside = true;
              }

              let collapsableGroup = false;
              let initialCollapsableState = false;
              let collapsableState = false;
              let collapsableTitle = "";

              formDefinition.map((formFieldDefinition: any, fieldIndex) => {
                if (formFieldDefinition.section !== undefined && section == formFieldDefinition.section) {
                  if (formFieldDefinition.type == "title") {                    
                    if (formGroupCssClass == "" && formFieldDefinition.groupCssClass) {
                      formGroupCssClass = formFieldDefinition.groupCssClass;
                    }
                    
                    if (!!formFieldDefinition.collapsable) {
                      collapsableGroup = true;
                      initialCollapsableState = !!formFieldDefinition.initialCollapsableState;
                      collapsableState = initialCollapsableState;
                      collapsableTitle = formFieldDefinition.label;
                      if (this.state.collapsableState[collapsableTitle] !== undefined) {
                        collapsableState = this.state.collapsableState[collapsableTitle];
                      }
                    }

                    if (formGroupCount > 1) {
                      if (index < sections.length - 1) {
                        
                        if (this.hasFormBlockIntelligence()) {
                          sectionBreakClass = "form__section-break";
                        }
                        
                        if (fieldIndex + 1 < formDefinition.length) {
                          if (formDefinition[fieldIndex + 1].isAdresBlokField) {
                            sectionBreakClass = "";
                          }
                        } 
                      }
                    }
                  }
                }
              });
              
              let CollapsableElement = <></>;
              if (!!collapsableGroup) {
                formGroupCssClass += " form__collapsable-section"; 
                CollapsableElement = <button onClick={this.onToggleGroupCollapsable.bind(this, collapsableTitle, initialCollapsableState)} 
                  className="form-group-collapse-heading d-flex btn w-100 flex-row align-items-center">
                    <label className="form__panel-title mb-0">{collapsableTitle}</label>
                    <span className={"icon ml-auto " + (collapsableState ? "icon-plus" : "icon-minus")}></span>
                  </button>;
              }
              let backToFormStepElement = this.createBackToStepLink(formDefinition, section, formikBag);
              
              let hasElements = false;
              let formElements = formDefinition.map((formFieldDefinition: any, index) => {
                let visible = true;
                let renderFormElement = false;

                if (formFieldDefinition.section === undefined && section == "") {
                  renderFormElement = true;                    
                } else
                if (formFieldDefinition.section !== undefined && section == formFieldDefinition.section) {
                  renderFormElement = true;
                } 

                if (formFieldDefinition.visibilityCondition !== undefined) {
                  formFieldDefinition.visibilityCondition.map((condition : any) => {

                    if (!this.isFieldVisible(formikBag, condition, formFieldDefinition)) {
                      visible = false;
                    } 
                  });
                }

                let visibilityClass = "";
                if (!visible) {
                  visibilityClass = " form__form-group-hidden";
                }
                
                if (renderFormElement && visible)                  
                {
                  hasElements = true;
                  let fieldType = formFieldDefinition.type; 
                
                  if (fieldType == "custom") {
                    let additionalCssClass = "";
                    if (formFieldDefinition.PlaceInFormArea !== undefined && !!formFieldDefinition.PlaceInFormArea) {
                      additionalCssClass = " form__custom-panel--" + Slug.fromString(formFieldDefinition.customType) + "--place-in-form-area";
                    }

                    additionalCssClass += " " + this.getAdditionalClassForCustomField(formFieldDefinition, formFieldDefinition.customType);

                    let fieldKey = this.getFieldKey(formFieldDefinition, formikBag);                   

                    return <div key={fieldKey} data-key={fieldKey}
                        className={"form__custom-panel form__custom-panel--" + Slug.fromString(formFieldDefinition.name) + 
                        " form__custom-panel--" + Slug.fromString(formFieldDefinition.customType) + additionalCssClass }>
                      {self.props.renderCustomField(formFieldDefinition, this, formikBag)}
                    </div>;
                  } else
                  if (fieldType == "title") {
                    let labelText = FormLabelHelpers.hasValue(formFieldDefinition.label, "@ismacro", "true")
                    ? formFieldDefinition.label["#text"]
                    : formFieldDefinition.label;

                    if (labelText != "" && labelText.indexOf("- hidden") < 0) {                      
                      let placeOutsideClass = "";
                      if (placeFirstTitlePanelInSectionOutside) {
                        placeOutsideClass = "form__panel--place-outside";
                        placeFirstTitlePanelInSectionOutside = false;
                      }
                      const renderFormError = !formErrorWasRendered;
                      formErrorWasRendered = true;

                      const label = StringHelpers.replacePropertiesInString(labelText, {...this.getPayload() , ...formikBag.values}, true);
                      
                      formTitleIndex++;

                      const hasSubtitle = this.props.formConfig.pageSettings[this.props.formName] && 
                        this.props.formConfig.pageSettings[this.props.formName].subtitle != undefined &&
                        this.props.formConfig.pageSettings[this.props.formName].subtitle != "";

                      const hasEmptyLabel = label.trim() == "";
                      
                      const formPanelClass = `form__panel form__panel-${formFieldDefinition.name} ${placeOutsideClass}
                          ${hasEmptyLabel && " form__panel--with-empty-title"}`;

                      const formTitleClass = `form__panel-title ${hasEmptyLabel && " form__panel-title-empty"}`;

                      return <div key={formFieldDefinition.name + "_formfield"}
                          className={formPanelClass}>                              
                          {(!!this.props.formConfig.showStepAsTitle || !!this.props.formConfig.addStepAsTitle) && 
                            (formTitleIndex <= 1) && <>
                              <h2 className={"form__step-title " + (hasSubtitle ? "form__step-title--withsubtitle" : "")}>{ReactHtmlParser(this.getCurrentStepCaption())}</h2>
                              {hasSubtitle &&
                                <h3 className="form__step-subtitle">{this.props.formConfig.pageSettings[this.props.formName].subtitle}</h3>}
                            </>}
                          {backToFormStepElement}                              
                          {(!this.props.formConfig.showStepAsTitle || (formTitleIndex > 1)) && !hasEmptyLabel &&
                            <h3 className={formTitleClass}>{label}</h3>}                            
                          {renderFormError && this.props.renderFormError !== undefined ? this.props.renderFormError() : null}
                      </div>;
                    }
                    return null;
                  } else
                  if (fieldType == "label") {
                    return <div key={formFieldDefinition.name + "_formfield"}
                        className={"form-group" + visibilityClass}>
                      <span className="form__static-text">{formFieldDefinition.label}</span>
                    </div>;
                  } else
                  if (fieldType != "submit") {

                    let fieldKey = this.getFieldKey(formFieldDefinition, formikBag);

                    return <Field key={fieldKey}
                      validate={self.validateField.bind(this, formFieldDefinition, formikBag)}
                      name={formFieldDefinition.name}
                    >
                      {({ field, form }: FieldProps<any>) => {
                          
                          if (field.value === undefined) {
                            
                            // als field.value undefined is.. dan wordt er uncontrolled-input gemaakt.
                            // en dat willen we niet.
                            // dit gebeurt bij het switchen tussen forms

                            return null;
                          }
                          
                          let fieldType = formFieldDefinition.type;
                          if (formFieldDefinition.enabledCondition !== undefined) {
                              if (!formFieldDefinition.enabledCondition.kenticoMacroFunction(this.form.fieldMapper, field.value, KenticoValidatorUtils)) {
                                if ((fieldType === "checkbox" && 
                                    (!this.props.formConfig.showCheckboxAsTextWhenDisabled)) || 
                                    (fieldType === "radio" &&
                                    formFieldDefinition.showRadioButtonInDisabledState !== undefined && 
                                    !!formFieldDefinition.showRadioButtonInDisabledState) ||
                                    (fieldType === "switch" &&
                                    formFieldDefinition.showSwitchInDisabledState !== undefined && 
                                    !!formFieldDefinition.showSwitchInDisabledState) ||
                                    (fieldType === "textarea" &&
                                    formFieldDefinition.showTextAreaInDisabledState !== undefined && 
                                    !!formFieldDefinition.showTextAreaInDisabledState)
                                 ) {
                                  formFieldDefinition.readOnly = true;
                                } else
                                if ((fieldType != "custom") && (fieldType !== "link")) {
                                  fieldType = "readOnly";                                    
                                }
                              } else {
                                if ((fieldType === "checkbox" && 
                                    (!this.props.formConfig.showCheckboxAsTextWhenDisabled)) || 
                                    (fieldType === "radio" &&
                                    formFieldDefinition.showRadioButtonInDisabledState !== undefined && 
                                    !!formFieldDefinition.showRadioButtonInDisabledState) ||
                                    (fieldType === "switch" &&
                                    formFieldDefinition.showSwitchInDisabledState !== undefined && 
                                    !!formFieldDefinition.showSwitchInDisabledState) ||
                                    (fieldType === "textarea" &&
                                    formFieldDefinition.showTextAreaInDisabledState !== undefined && 
                                    !!formFieldDefinition.showTextAreaInDisabledState) 
                                 ) {
                                  formFieldDefinition.readOnly = false;
                                }
                              }
                          }

                          if (!!formFieldDefinition.disableWhenFieldHasUrlParam &&
                            urlParams[formFieldDefinition.name] !== undefined) {
                            if (urlParams[formFieldDefinition.name] !== "") {
                              fieldType = "readOnly";
                            }
                          }

                          if (formFieldDefinition.enabledOnError &&
                              formFieldDefinition.enabledOnError === 'true' &&
                                formikBag.values[
                                  'error-' + formFieldDefinition.name
                                  ]) {
                            fieldType = formFieldDefinition.type;
                          }

                          if (this.props.readOnly) {
                            if (((fieldType === "checkbox" && 
                                (!this.props.formConfig.showCheckboxAsTextWhenDisabled)) || 
                                (fieldType === "radio" &&
                                 formFieldDefinition.showRadioButtonInDisabledState !== undefined && 
                                 !!formFieldDefinition.showRadioButtonInDisabledState)
                            )) {
                              formFieldDefinition.readOnly = true;
                            } else
                            if ((fieldType !== "custom") && (fieldType !== "link")) {
                              fieldType = "readOnly";                                
                            }
                          }

                          let formControlComponentName = FormControlsHelper.getFormControlComponent(fieldType);
                          if (formControlComponentName !== undefined) {
                            let FormControl : any = FormControls[formControlComponentName];
                            if (FormControl !== undefined) {                
                              const renderFormControl = () => <FormControl 
                                  key={"control-" + fieldKey}
                                  formController={this}
                                  visibilityClass={visibilityClass}
                                  formFieldDefinition={formFieldDefinition}
                                  form={form} 
                                  field={field}
                                  formikBag={formikBag}
                                  forms={this.props.formValues}
                                  formConfig={this.props.formConfig}
                                  isSubmitting={this.props.isSubmitting}
                                ></FormControl>;
                              return <>{renderFormControl()}</>;
                            }
                          }
                          return null;                            
                        }
                      }
                    </Field>;
                  }
                }
              }
            );

            if (!hasElements) {
              return null;
            }
            
            return <React.Fragment  key={"section-fragment_" + index}>
              {CollapsableElement}                
              {!collapsableState && <div key={"section_" + index} 
              className={"form__section " +
              "form__section" + (section == "" ? "-default" : "-" + sectionCssClass) + " " + 
              sectionBreakClass + " " +
              formGroupCssClass }>{formElements}              
              </div>}
            </React.Fragment>;
            })
            }
            {this.renderSubmitButton(disableSubmitButtonOnValidationError, formikBag, submitLabel, submitCssClass)}
            {this.renderOptionalExtraButton(disableSubmitButtonOnValidationError, formikBag)}
          </Form>);
          }
        }
      />
      </div>;
  }

  static contextType = MijnFNVPageContext;
  context!: React.ContextType<typeof MijnFNVPageContext>;
}

